import { useMemo } from 'react';
import Select from 'react-select';
import styles from './ApkSelect.module.scss';

export const formatApkVersion = apk => {
  if (!apk) {
    return '';
  }
  return apk.version_name
    ? `v${apk.version_name} (code ${apk.versioncode})`
    : `code ${apk.versioncode}`;
};

const buildOption = apk => ({
  value: String(apk.versioncode),
  // Plain-text label drives react-select's search filter and screen readers.
  label: [
    apk.version_name ? `v${apk.version_name}` : null,
    `code ${apk.versioncode}`,
    apk.package_name,
    apk.filename,
  ]
    .filter(Boolean)
    .join(' '),
  apk,
});

export function ApkOptionLabel({ apk, compact = false }) {
  return (
    <div className={`${styles.option} ${compact ? styles.compact : ''}`}>
      <span className={styles.headline}>
        <strong>{apk.version_name ? `v${apk.version_name}` : 'Unknown version'}</strong>
        <em title="Version code">{apk.versioncode}</em>
        {apk.missing && <b>file missing</b>}
      </span>
      <span className={styles.subline}>
        <small>
          {apk.package_name || (apk.missing ? 'Not in the APK library' : 'Unknown package')}
        </small>
        {apk.filename && <small className={styles.filename}>{apk.filename}</small>}
      </span>
    </div>
  );
}

function ApkSelect({
  inputId,
  apks,
  value,
  onChange,
  placeholder = 'Choose an APK',
  isDisabled = false,
}) {
  const options = useMemo(() => apks.map(buildOption), [apks]);
  const selectedValue = value === undefined || value === null ? '' : String(value);

  const { selectedOption, allOptions } = useMemo(() => {
    const match = options.find(option => option.value === selectedValue) || null;
    if (match || !selectedValue) {
      return { selectedOption: match, allOptions: options };
    }
    // The current target is not in the library any more: keep it selectable so the
    // control still reflects the stored value instead of silently switching.
    const missing = buildOption({
      versioncode: selectedValue,
      filename: `terna_app_v${selectedValue}.apk`,
      missing: true,
    });
    return { selectedOption: missing, allOptions: [missing, ...options] };
  }, [options, selectedValue]);

  return (
    <Select
      inputId={inputId}
      classNamePrefix="apkSelect"
      options={allOptions}
      value={selectedOption}
      onChange={option => onChange(option ? option.value : '')}
      formatOptionLabel={(option, { context }) => (
        <ApkOptionLabel apk={option.apk} compact={context === 'value'} />
      )}
      isSearchable
      isDisabled={isDisabled}
      placeholder={placeholder}
      noOptionsMessage={() => 'No matching APK'}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      maxMenuHeight={360}
    />
  );
}

export default ApkSelect;
